import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Factory, LineChart, ShieldCheck, Zap, Layers, Lock } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';

const ProductCard = ({ to, badge, name, tagline, features, gradient, icon: Icon, footer }) => (
  <Link
    to={to}
    className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-8 hover:border-white/20 hover:bg-white/[0.04] transition-all duration-300 overflow-hidden"
  >
    <div
      aria-hidden
      className="absolute inset-0 opacity-40 group-hover:opacity-70 transition-opacity duration-500 pointer-events-none"
      style={{ background: gradient }}
    />
    <div className="relative">
      <div className="flex items-start justify-between">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-white/70">{badge}</span>
        </div>
        <div className="h-12 w-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center backdrop-blur">
          <Icon className="w-5 h-5 text-white/80" />
        </div>
      </div>

      <h3 className="mt-8 text-3xl md:text-4xl font-semibold text-white tracking-tight">{name}</h3>
      <p className="mt-3 text-white/60 leading-relaxed max-w-md">{tagline}</p>

      <ul className="mt-6 space-y-2.5">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-white/75">
            <span className="mt-1.5 h-1 w-1 rounded-full bg-cyan-300 flex-shrink-0" />
            {f}
          </li>
        ))}
      </ul>

      <div className="mt-8 flex items-center justify-between pt-6 border-t border-white/10">
        <span className="text-[12px] text-white/50">{footer}</span>
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:gap-3 transition-all">
          Open estimator
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </div>
  </Link>
);

const Landing = () => {
  const { user, isAdmin } = useAuth();
  const { brand, brandKey } = useBrand();
  const canStreamline = !user || isAdmin || (user.allowed_products || ['pti', 'core', 'streamline']).includes('streamline');
  const canCore = !user || isAdmin || (user.allowed_products || ['pti', 'core', 'streamline']).includes('core');
  const coreTileName = brandKey === 'streamline' ? 'Streamline Core' : 'Itanta Core';
  const coreTileBadge = brandKey === 'streamline' ? 'Streamline Core Platform' : 'Itanta Core Platform';
  const adminLabel = brandKey === 'streamline' ? 'Streamline admin' : 'Itanta admin';
  return (
    <div className="min-h-screen bg-[#0a0a0d] text-white relative overflow-x-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(1200px 500px at 15% -10%, rgba(34,211,238,0.10), transparent 60%), radial-gradient(1000px 600px at 90% 5%, rgba(168,85,247,0.10), transparent 60%)',
        }}
      />
      <Header />

      <main className="relative max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        {/* Hero */}
        <section className="pt-16 md:pt-24">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-emerald-300" />
            <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-white/70">
              {brand.studioEyebrow}
            </span>
          </div>
          <h1 className="mt-6 text-4xl md:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl">
            Choose your product to{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: brand.gradient }}
            >
              size, price & quote
            </span>{' '}
            in minutes.
          </h1>
          <p className="mt-5 max-w-2xl text-white/60 leading-relaxed text-[15px]">
            {brand.heroDescriptor}, apply region &amp; customer-specific price lists, generate a
            branded quotation with editable terms, and share it.
          </p>
        </section>

        {/* Product grid */}
        <section className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {canStreamline && (
          <ProductCard
            to="/streamline"
            badge="Streamline360 · Prompt-to-Insights"
            name="Streamline360"
            tagline="Hybrid concurrent-licensing model for prompt-driven insights on the manufacturing floor."
            features={[
              '4 tiers · Lite → Business → Professional → Enterprise',
              'Concurrent creator & view-only seats',
              'Historian connectors (AVEVA PI, IP21, Canary, GE Proficy)',
              'Domain-knowledge dashboard packs',
              'MRR & ARR calculated instantly',
            ]}
            gradient="radial-gradient(600px 300px at 20% 0%, rgba(52,211,153,0.20), transparent 60%), radial-gradient(500px 300px at 100% 100%, rgba(34,211,238,0.16), transparent 60%)"
            icon={LineChart}
            footer="Perpetual + subscription · view-only add-ons"
          />
          )}
          {canCore && (
          <ProductCard
            to="/core"
            badge={coreTileBadge}
            name={coreTileName}
            tagline="Perpetual license platform with add-on modules. Region & customer-specific price lists supported."
            features={[
              '4 license tiers · Start → Grow → Scale → Infinity',
              '256 to Unlimited tags · 1 to 20 web clients',
              'Optional modules: AI Formula, Alarms, Redundancy …',
              '20% AMC included in estimate',
              'Region &amp; customer price lists',
            ]}
            gradient="radial-gradient(600px 300px at 80% 0%, rgba(168,85,247,0.22), transparent 60%), radial-gradient(500px 300px at 0% 100%, rgba(34,211,238,0.14), transparent 60%)"
            icon={Layers}
            footer="Perpetual license + 20% annual AMC"
          />
          )}
          {!canStreamline && !canCore && (
            <div className="md:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
              <Lock className="w-6 h-6 text-white/40 mx-auto mb-3" />
              <div className="text-white font-semibold">No products assigned</div>
              <div className="mt-1 text-white/55 text-[13.5px]">Your account has no products enabled. Contact your {adminLabel}.</div>
            </div>
          )}
        </section>

        {/* Value chips */}
        <section className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Factory, label: 'Built for manufacturing floors' },
            { icon: Zap, label: 'Real-time MRR & ARR calc' },
            { icon: ShieldCheck, label: 'Region-based T&Cs and tax' },
            { icon: Sparkles, label: 'Branded quote PDFs' },
          ].map((v, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-4 flex items-center gap-3">
              <span className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                <v.icon className="w-4 h-4 text-cyan-300" />
              </span>
              <span className="text-[13px] text-white/75 leading-snug">{v.label}</span>
            </div>
          ))}
        </section>

        {/* Footer */}
        <footer className="mt-24 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-[12px] text-white/40">
            © {new Date().getFullYear()} {brand.companyLegalName} · Pricing Studio
          </div>
          <div className="flex items-center gap-6 text-[12px] text-white/40">
            <a href="#" className="hover:text-white/70 transition-colors">Terms</a>
            <a href="#" className="hover:text-white/70 transition-colors">Privacy</a>
            <a href="#" className="hover:text-white/70 transition-colors">Contact</a>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Landing;
